"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import type { Ad as AdType } from "../../types/ads/adResource";
import type { CardsResponse } from "../../types/content";
import type { FeedItems } from "../../types/content/feedItem";
import type { InjectableSnapshot } from "../../types/injectables";
import type { ListView } from "../../types/webTypes";

import { fetchFeedFromUrl } from "../../services/fetchFeed";
import { addInjectablesToFeed } from "../../utils/addInjecteablesToFeed";
import { astTagCreator } from "../Ad/utils/helpers/astTagCreator";
import { batcher } from "../Ad/utils/helpers/batcher";
import { DotLoader } from "../DotLoader/DotLoader";
import { Feed } from "../Feed/Feed";
import styles from "./FeedLoader.module.scss";
import { omitRepeated } from "./util/omitRepeated";

const FeedLoader = ({
  children,
  injectables,
  fetchFeedUrl,
  ads,
  feedLayoutType,
  initialItems,
  listView,
  headerTitle,
  cards,
  isLoggedIn,
}: React.PropsWithChildren<{
  children?: React.ReactNode;
  cards?: CardsResponse;
  injectables?: InjectableSnapshot | null;
  fetchFeedUrl: string;
  ads?: {
    fullscreenScrollAd?: AdType;
    wideScreenSideAd?: AdType;
    wideScreenSideAd2?: AdType;
    sponsoredBannerAdResource?: AdType;
  };
  feedLayoutType: "top" | "latest" | "list";
  listView?: ListView;
  initialItems: FeedItems;
  headerTitle?: string;
  isLoggedIn: boolean;
}>) => {
  const ref = useRef(null);
  const [feedData, setFeedData] = useState<FeedItems>([]);
  const [loading, setLoading] = useState(false);
  const [fetchUrl, setFetchUrl] = useState(fetchFeedUrl);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [noMoreArticles, setNoMoreArticles] = useState(false);
  const [injectablesSnapshot, setInjectablesSnapshot] = useState(injectables);

  const { wideScreenSideAd, wideScreenSideAd2, sponsoredBannerAdResource } =
    ads || {};

  const loadMoreFeedData = useCallback(
    async (url: string) => {
      setLoading(true);
      const fetchedFeedData = await fetchFeedFromUrl({ path: url, isLoggedIn });
      if (!fetchedFeedData?.data) {
        setLoading(false);
        return;
      }

      if (
        !fetchedFeedData.error &&
        fetchedFeedData.data.articles.length === 0 &&
        initialItems.length > 0
      ) {
        console.log("No more articles to load");
        setNoMoreArticles(true);
        return;
      }

      let newFeedData = fetchedFeedData.data.articles;
      let newInjectableSnapshot = injectablesSnapshot;
      const haveInjectableConfigPositions =
        Array.isArray(injectablesSnapshot?.slots) &&
        injectablesSnapshot.slots.length > 0;

      setFeedData((currentFeedData) => {
        const filteredData = omitRepeated(
          currentFeedData,
          newFeedData,
          initialItems,
          true,
        );

        if (haveInjectableConfigPositions) {
          [newFeedData, newInjectableSnapshot] = addInjectablesToFeed(
            filteredData,
            injectablesSnapshot,
          );

          if (
            Array.isArray(newInjectableSnapshot?.placeholderUsed) &&
            newInjectableSnapshot.placeholderUsed.length > 0
          ) {
            batcher(astTagCreator(newInjectableSnapshot.placeholderUsed));
          }

          return [...currentFeedData, ...newFeedData];
        }

        return [...currentFeedData, ...filteredData];
      });

      setFetchUrl(fetchedFeedData.data.links.next || "");
      setInjectablesSnapshot(newInjectableSnapshot);
      setLoading(false);
    },
    [initialItems, injectablesSnapshot, isLoggedIn],
  );

  useEffect(() => {
    const handleIntersection = ([entry]: IntersectionObserverEntry[]) => {
      if (entry.isIntersecting && fetchUrl && !loading && !shouldFetchData) {
        setShouldFetchData(true);
      }
    };
    const observer = new IntersectionObserver(handleIntersection);

    const scrollLimitElement = ref.current;
    if (scrollLimitElement) {
      observer.observe(scrollLimitElement);
    }

    return () => {
      if (scrollLimitElement) {
        observer?.unobserve(scrollLimitElement);
      }
      observer.disconnect();
    };
  }, [loading, fetchUrl, shouldFetchData]);

  useEffect(() => {
    if (shouldFetchData && !loading && fetchUrl) {
      setShouldFetchData(false);
      loadMoreFeedData(fetchUrl);
    }
  }, [shouldFetchData, fetchUrl, loading, loadMoreFeedData]);

  if (!fetchFeedUrl) return <>{children ? children : null}</>;

  return (
    <div className={styles.feedLoaderContainer}>
      {feedLayoutType === "list" ? (
        <Feed
          data={[...initialItems, ...feedData]}
          wideScreenSideAd={wideScreenSideAd}
          sponsoredBannerAdResource={sponsoredBannerAdResource}
          layoutType={feedLayoutType}
          listView={listView}
          cards={cards}
          isLoggedIn={isLoggedIn}
          isFirstFeed={Array.isArray(initialItems) && initialItems.length > 0}
          endOfFeed={noMoreArticles}
          headerTitle={headerTitle || ""}
        />
      ) : (
        <>
          {children}
          {feedData && (
            <Feed
              data={feedData}
              wideScreenSideAd={wideScreenSideAd}
              wideScreenSideAd2={wideScreenSideAd2}
              isLoggedIn={isLoggedIn}
              layoutType={feedLayoutType}
              endOfFeed={noMoreArticles}
              headerTitle={headerTitle || ""}
            />
          )}
        </>
      )}

      {noMoreArticles ? (
        <div className={styles.endOfFeed} />
      ) : (
        <>
          <div className={styles.loader}>{loading && <DotLoader />}</div>
          <div ref={ref} className={styles.feedLoadLimit}></div>
        </>
      )}
    </div>
  );
};

export { FeedLoader };
